import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  FormControl,
  InputLabel,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Person as PersonIcon,
  Lock as LockIcon,
  AdminPanelSettings as RoleIcon,
  ArrowBack as ArrowBackIcon,
  Business as BranchIcon,
} from "@mui/icons-material";
import ResponsiveAppBar from "../components/AppBar";
import { API_BASE_URL } from "../api/config";

// MUI Theme (unchanged)
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' },
    secondary: { main: '#925f96' },
    background: { default: '#f8fafc' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    success: { main: '#77c8c7' },
    error: { main: '#d32f2f' },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '0.5px',
      background: 'linear-gradient(135deg, #77c8c7 0%, #925f96 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    h6: {
      fontWeight: 600,
      color: '#334155',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.1rem',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(119, 200, 199, 0.15)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #77c8c7 0%, #925f96 100%)',
          color: '#ffffff',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover fieldset': {
              borderColor: '#77c8c7',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#925f96',
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.05)',
          transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
          opacity: 0,
          transform: 'translateY(20px)',
          animation: 'fadeInUp 0.5s forwards',
        },
      },
    },
  },
});

// CSS Keyframes
const styles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const AddUserPage = () => {
  const [userDetails, setUserDetails] = useState({
    userId: "",
    password: "",
    name: "",
    role: "User",
    branchId: "", // Added branchId
  });
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ open: false, message: "", severity: "" });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    if (!userDetails.userId.trim()) newErrors.userId = "User ID is required";
    if (!userDetails.name.trim()) newErrors.name = "Name is required";
    if (!userDetails.password.trim()) newErrors.password = "Password is required";
    if (!userDetails.role) newErrors.role = "Role is required";
    if (!userDetails.branchId) newErrors.branchId = "Branch is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setAlert({ open: false, message: "", severity: "" });
    try {
      const user = JSON.parse(sessionStorage.getItem("user"));
      console.log("AddUserPage - Submitting:", { userDetails, token: user?.token });
      const response = await axios.post(
        `${API_BASE_URL}/user/add_user`,
        userDetails,
        {
          headers: { Authorization: `Bearer ${user?.token}` },
        }
      );
      console.log("AddUserPage - Response:", response.data);
      setAlert({ open: true, message: "User added successfully!", severity: "success" });
      setUserDetails({ userId: "", password: "", name: "", role: "User", branchId: "" });
    } catch (error) {
      console.error("AddUserPage - Error:", error.response?.data);
      setAlert({
        open: true,
        message: error.response?.data?.message || "Error adding user",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Role-based access control
  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    console.log("AddUserPage - User:", user);
    if (!user || !["Admin Manager", "Branch Admin"].includes(user.role)) {
      console.log("AddUserPage - Redirecting to /admin: Insufficient role");
      navigate("/admin");
    }
  }, [navigate]);

  const handleAlertClose = () => {
    setAlert({ open: false, message: "", severity: "" });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <style>{styles}</style>
      <ResponsiveAppBar />
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <IconButton onClick={() => navigate("/admin")} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1">
                Add New User
              </Typography>
            </Box>
            <Divider sx={{ mb: 3, bgcolor: 'primary.light' }} />
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="User ID"
                    name="userId"
                    value={userDetails.userId}
                    onChange={handleInputChange}
                    required
                    error={!!errors.userId}
                    helperText={errors.userId}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Name"
                    name="name"
                    value={userDetails.name}
                    onChange={handleInputChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    InputProps={{
                      startAdornment: <PersonIcon sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={userDetails.password}
                    onChange={handleInputChange}
                    required
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: <LockIcon sx={{ mr: 1, color: '#64748b' }} />,
                    }}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={userDetails.role}
                      onChange={handleInputChange}
                      label="Role"
                      startAdornment={<RoleIcon sx={{ mr: 1, color: '#64748b' }} />}
                      required
                    >
                      <MenuItem value="Client">Client</MenuItem>
                      <MenuItem value="Branch Admin">Branch Admin</MenuItem>
                      <MenuItem value="Admin Manager">Admin Manager</MenuItem>
                    </Select>
                    {errors.role && <Typography color="error" variant="caption">{errors.role}</Typography>}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined" error={!!errors.branchId}>
                    <InputLabel>Branch</InputLabel>
                    <Select
                      name="branchId"
                      value={userDetails.branchId}
                      onChange={handleInputChange}
                      label="Branch"
                      startAdornment={<BranchIcon sx={{ mr: 1, color: '#64748b' }} />}
                      required
                    >
                      <MenuItem value="branchOne">Elampillai</MenuItem>
                      <MenuItem value="branchTwo">Attayampatti</MenuItem>
                    </Select>
                    {errors.branchId && <Typography color="error" variant="caption">{errors.branchId}</Typography>}
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={isLoading}
                    sx={{ py: 1.5 }}
                  >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : "Add User"}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1" color="text.secondary">
            Velavan Finance - Your Trusted Partner in Financial Growth
          </Typography>
        </Box>
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleAlertClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleAlertClose} severity={alert.severity} sx={{ width: '100%' }}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </ThemeProvider>
  );
};

export default AddUserPage;